import React, {
  useContext,
  useState,
  memo,
  useCallback,
  useEffect,
} from 'react';
import T from 'i18n-react';

import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import FormHelperText from '@material-ui/core/FormHelperText';

import type { PlanTermWorkingTimeProps } from '../../../../../core/Plans/context';
import WorkingTime, { WorkingTimeProps } from './WorkingTime';
import WorkingHour from './WorkingHour';
import OverTimeCheckbox from './OverTimeCheckbox';
import PlanTermContext from '../../../../../core/Plans/PlanTerm/context';
import {
  validateWorkingTime,
  validateWorkingHour,
} from '../../../../../core/Plans/PlanTerm/validateSchedule';
import type { WorkingTimeChangeData } from './WorkingTime';
import {
  convertTimeText,
  convertHourText,
} from '~/src/admin/pages/Plan/hooks/useWorkingTime';

const useStyles = makeStyles((theme) => ({
  button: {
    textAlign: 'center',
    padding: theme.spacing(1, 0, 2.5),
  },
}));

const makeSchedule = (): PlanTermWorkingTimeProps => ({
  startTime: null,
  endTime: null,
  workingTime: null,
});

type WorkingTimesProps = WorkingTimeProps & {
  times: PlanTermWorkingTimeProps[];
};

type WorkingTimeValidity = {
  startTime: boolean;
  endTime: boolean;
  workingTime: boolean;
  workingTimeValidity?: boolean;
};
const WorkingTimes = memo(
  ({ times, onChange, onDelete, workingHours }: WorkingTimesProps) => {
    return times.map(
      (
        {
          id,
          startTimeText,
          endTimeText,
          workingTime,
          startTimeValidity,
          endTimeValidity,
          workingTimeValidity,
        },
        index,
      ) => (
        <Grid item key={`${id || 'noId'}-${index}`}>
          <WorkingTime
            index={index}
            startTime={startTimeText ? startTimeText : ''}
            endTime={endTimeText ? endTimeText : ''}
            workingTime={workingTime ? workingTime : ''}
            startTimeValidity={startTimeValidity}
            endTimeValidity={endTimeValidity}
            workingTimeValidity={workingTimeValidity}
            workingHours={workingHours}
            onChange={onChange}
            onDelete={times.length > 1 ? onDelete : undefined}
          />
        </Grid>
      ),
    );
  },
);

const Schedule = () => {
  const classes = useStyles();
  const { planTerm, setPlanTerm } = useContext(PlanTermContext);

  const [hasOvertimeWork, setHasOverTimeWork] = useState(
    planTerm.hasOvertimeWork || false,
  );

  const [workingTimes, setWorkingTimes] = useState(() => {
    let times;
    times =
      planTerm.workingTimes?.length > 0
        ? [...planTerm.workingTimes]
        : [makeSchedule()];

    times.forEach((time) => {
      // 型定義がうまくいかないため、一旦anyで定義
      const validity: any = validateWorkingTime(time, true);
      time.startTimeValidity = validity.startTime;
      time.endTimeValidity = validity.endTime;
      time.workingTimeValidity = validity.workingTime;
    });

    return times;
  });

  const [workingHours, setWorkingHours] = useState(() => {
    let hours;
    hours = {
      startTime: planTerm.startTimeText ? planTerm.startTimeText : '',
      endTime: planTerm.endTimeText ? planTerm.endTimeText : '',
      totalTimes: planTerm.totalTimes ? planTerm.totalTimes : '',
    };

    // 型定義がうまくいかないため、一旦anyで定義
    const validity: any = validateWorkingHour(hours, true);
    hours.startTimeValidity = validity.startTime;
    hours.endTimeValidity = validity.endTime;
    hours.totalTimesValidity = validity.totalTimes;

    return hours;
  });

  const handleOverTimeWorkChange = useCallback(() => {
    setHasOverTimeWork((prevState) => {
      const hasOvertimeWork = !prevState;
      setPlanTerm((prevState) => ({ ...prevState, hasOvertimeWork }));
      return hasOvertimeWork;
    });
  }, [setPlanTerm]);

  const handleAdd = useCallback(() => {
    setWorkingTimes((prevState) => [...prevState, makeSchedule()]);
  }, [setWorkingTimes]);

  const handleDelete = useCallback(
    (index) => {
      setWorkingTimes((prevState) => {
        const times = [...prevState];
        times.splice(index, 1);
        return times;
      });
    },
    [setWorkingTimes],
  );

  const handleWorkingHourChange = useCallback(
    (workingHours: WorkingTimeChangeData) => {
      setWorkingHours((prevState) => {
        const hours = { ...prevState, ...workingHours };
        return hours;
      });
    },
    [setWorkingHours],
  );

  const handleWorkingTimeChange = useCallback(
    ({ index, ...workingTime }: WorkingTimeChangeData) => {
      setWorkingTimes((prevState) => {
        const workingTimes = prevState.map((v, mapIndex) => {
          if (index === mapIndex) return { ...v, ...workingTime };
          return v;
        });
        return workingTimes;
      });
    },
    [setWorkingTimes],
  );

  useEffect(() => {
    setPlanTerm(
      (prevState) => ({
        ...prevState,
        startTime: workingHours.startTime,
        startTimeText: convertTimeText(workingHours.startTime, false),
        endTime: workingHours.endTime,
        endTimeText: convertTimeText(workingHours.endTime, true),
        totalTimes: workingHours.totalTimes,
        workingTimes: workingTimes.map((time: WorkingTimeChangeData) => {
          const { id, startTime, endTime, workingTime, planTermWorkingTimeId } =
            time;
          const startTimeText = convertTimeText(startTime, false);
          const endTimeText = convertTimeText(endTime, true);
          const workingTimeText = convertHourText(workingTime);
          return {
            id,
            startTime,
            endTime,
            startTimeText,
            endTimeText,
            workingTime,
            workingTimeText,
            planTermWorkingTimeId,
          };
        }),
      }),
      {
        workingHours:
          workingHours.startTimeValidity &&
          workingHours.endTimeValidity &&
          workingHours.totalTimesValidity,
        workingTimes: workingTimes.every(
          ({ startTimeValidity, endTimeValidity, workingTimeValidity }) =>
            startTimeValidity && endTimeValidity && workingTimeValidity,
        ),
      },
    );
  }, [workingTimes, workingHours]);

  return (
    <Grid container spacing={1} direction="column">
      <Typography>{T.translate('planPage.planTerms.hours')}</Typography>
      <FormHelperText>
        {T.translate('planPage.planTerms.hoursHelp')}
      </FormHelperText>
      <WorkingHour
        startTime={workingHours.startTime}
        endTime={workingHours.endTime}
        totalTimes={workingHours.totalTimes}
        startTimeValidity={workingHours.startTimeValidity}
        endTimeValidity={workingHours.endTimeValidity}
        totalTimesValidity={workingHours.totalTimesValidity}
        onChange={handleWorkingHourChange}
      />
      <Typography>{T.translate('planPage.planTerms.schedule')}</Typography>
      <FormHelperText>
        {T.translate('planPage.planTerms.scheduleHelp')}
      </FormHelperText>
      <WorkingTimes
        workingHours={workingHours}
        times={workingTimes}
        onChange={handleWorkingTimeChange}
        onDelete={handleDelete}
      />
      <Grid className={classes.button}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleAdd}
        >
          {T.translate('addX', {
            x: T.translate('planPage.planTerms.schedule'),
          })}
        </Button>
      </Grid>
      <OverTimeCheckbox
        onChange={handleOverTimeWorkChange}
        checked={hasOvertimeWork}
      />
    </Grid>
  );
};

export default Schedule;
